import { useCallback, useSyncExternalStore } from 'react';

/** Tailwind `md` / the `--breakpoint-tablet` token (768px) — anything narrower is "mobile". */
const MOBILE_QUERY = '(max-width: 767px)';

/**
 * `true` when the viewport is below the tablet breakpoint (768px) — i.e. phone width.
 * Viewport-driven, not platform detection (a narrow desktop window counts as mobile).
 *
 * This is the single, sanctioned JS breakpoint check. Prefer CSS (`sm:`/`md:` classes) for
 * responsive *styling*; reach for this only to swap component trees or change behavior (e.g.
 * `ResponsiveModal`'s sheet-vs-dialog). `MOBILE_QUERY` mirrors the `--breakpoint-tablet` token in
 * `layout.css` — keep them in sync, since Tailwind v4 keeps breakpoints in CSS (JS can't import
 * the value). Mirrors shadcn's `useIsMobile`, but on `useSyncExternalStore` so the first render
 * reads the correct value (no desktop→mobile flash).
 */
export function useIsMobile(): boolean {
  const subscribe = useCallback((onStoreChange: () => void) => {
    const mql = window.matchMedia(MOBILE_QUERY);
    mql.addEventListener('change', onStoreChange);
    return () => mql.removeEventListener('change', onStoreChange);
  }, []);

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false,
  );
}
